/* =================================================================
   norsu "Drop a hint" — sender side
   File: assets/norsu-drop-hint.js   Pairs with snippets/norsu-drop-hint.liquid

   Someone who wants a gift fills in the modal; we fire a "Dropped a Hint"
   event through Klaviyo's PUBLIC /client/events/ endpoint (no private key —
   same door assets/norsu-signup-popup.js uses) and a Klaviyo flow emails the
   person who'll be buying. The link we hand Klaviyo carries its own state in
   the query string, so there is no server, no KV and nothing to expire —
   identical approach to the wishlist "share my wishlist" link.

   Landing page that the link opens: sections/norsu-hint-landing.liquid.
   ================================================================= */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var METRIC = 'Dropped a Hint';
  var WISHLIST_KEY = 'norsu_wishlist';
  var NAME_KEY = 'norsu_hint_from';          /* remember the sender's own name */

  var modal, cfg, state = { items: [], source: 'pdp', url: '' };

  /* ---------- helpers ---------------------------------------------- */

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function validEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());
  }

  function absolute(u) {
    if (!u) return '';
    if (u.indexOf('//') === 0) return 'https:' + u;
    if (u.indexOf('http') === 0) return u;
    return location.origin + u;
  }

  function thumb(url, size) {
    if (!url) return '';
    return absolute(url).replace(/(\.[a-z]{3,4})(\?|$)/i, '_' + (size || 540) + 'x$1$2');
  }

  function wishlistHandles() {
    /* A shared ?items= view takes precedence — you may be hinting from a list
       someone sent you, which is never in your own localStorage. */
    var shared = new URLSearchParams(location.search).get('items');
    if (shared) {
      return shared.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
    }
    try {
      var v = JSON.parse(localStorage.getItem(WISHLIST_KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }

  /* The live variant on a PDP. The theme keeps the selected variant in the
     product form's hidden id input, so read it at submit time rather than at
     render time — the shopper may change colour/size after opening the modal. */
  function selectedVariant(trigger) {
    var scope = trigger && trigger.closest('.norsu-pdp, .product, main, body');
    var input = (scope || document).querySelector('form[action*="/cart/add"] [name="id"]')
             || document.querySelector('form[action*="/cart/add"] [name="id"]')
             || document.querySelector('[name="id"]');
    var v = input && input.value ? String(input.value).trim() : '';
    return /^\d+$/.test(v) ? v : '';
  }

  /* ---------- build the hint link ---------------------------------- */

  function hintUrl(fields) {
    var q = new URLSearchParams();
    q.set('p', state.items.map(function (i) { return i.handle; }).join(','));
    var vs = state.items.map(function (i) { return i.variantId || ''; });
    if (vs.some(Boolean)) q.set('v', vs.join(','));
    if (fields.from) q.set('f', fields.from);
    if (fields.to) q.set('t', fields.to);
    if (fields.note) q.set('m', fields.note);
    if (state.source && state.source !== 'pdp') q.set('s', state.source);
    return location.origin + (cfg.landing || '/pages/a-hint') + '?' + q.toString();
  }

  /* ---------- product lookup (for the email's own content) --------- */

  function lookup(items) {
    return Promise.all(items.map(function (it) {
      return fetch('/products/' + it.handle + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (p) {
          var variant = null;
          if (it.variantId) {
            variant = (p.variants || []).filter(function (v) { return String(v.id) === String(it.variantId); })[0] || null;
          }
          return {
            handle: it.handle,
            variantId: it.variantId || '',
            Name: p.title,
            VariantName: variant && variant.title && variant.title !== 'Default Title' ? variant.title : '',
            Price: ((variant ? variant.price : p.price) || 0) / 100,
            PriceFormatted: money(variant ? variant.price : p.price),
            ImageURL: thumb(p.featured_image || (p.images && p.images[0]), 540),
            URL: absolute(p.url),
            Available: variant ? !!variant.available : !!p.available
          };
        })
        .catch(function () { return null; });
    })).then(function (rows) { return rows.filter(Boolean); });
  }

  /* ---------- Klaviyo ---------------------------------------------- */

  function sendEvent(fields, products, url) {
    var total = products.reduce(function (a, p) { return a + (Number(p.Price) || 0); }, 0);
    var first = products[0] || {};
    var props = {
      SenderName: fields.from,
      RecipientName: fields.to,
      Note: fields.note || '',
      HasNote: !!fields.note,
      HintURL: url,
      Source: state.source === 'wishlist' ? 'Wishlist' : 'Product page',
      ItemCount: products.length,
      IsMultiple: products.length > 1,
      /* Flat fields so a single-item email needs no loop in Klaviyo */
      ProductName: first.Name || '',
      ProductVariant: first.VariantName || '',
      ProductURL: first.URL || '',
      ProductImage: first.ImageURL || '',
      ProductPrice: first.PriceFormatted || '',
      ItemNames: products.map(function (p) { return p.Name; }).join(', '),
      Items: products,
      $value: Math.round(total * 100) / 100
    };

    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: METRIC } } },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: fields.email,
                first_name: fields.to,
                properties: {
                  /* Flags so these addresses can be segmented OUT of marketing —
                     they never opted in, they were hinted at. */
                  hint_recipient: true,
                  hint_last_from: fields.from,
                  hint_last_at: new Date().toISOString()
                }
              }
            }
          }
        }
      }
    };

    return fetch(
      'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.companyId),
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body)
      }
    ).then(function (res) {
      if (res.status === 202 || res.ok) return true;
      return res.text().then(function (t) { throw new Error('Klaviyo ' + res.status + ': ' + t); });
    });
  }

  /* ---------- modal plumbing --------------------------------------- */

  function step(name) {
    modal.querySelectorAll('[data-ndh-step]').forEach(function (s) {
      s.setAttribute('data-active', s.getAttribute('data-ndh-step') === name ? 'true' : 'false');
    });
  }

  var lastFocus = null;

  function open() {
    lastFocus = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    /* Force a synchronous reflow so the transition has a start value, then flip.
       requestAnimationFrame is paused in background/hidden tabs, which left the
       card stuck at opacity:0 — a reflow read never is. */
    void modal.offsetHeight;
    modal.setAttribute('data-open', 'true');
    var f = modal.querySelector('[data-ndh-step][data-active="true"] input, [data-ndh-step][data-active="true"] button');
    if (f) setTimeout(function () { try { f.focus(); } catch (e) {} }, 120);
  }

  function close() {
    modal.setAttribute('data-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    setTimeout(function () { modal.hidden = true; }, 200);
    if (lastFocus) { try { lastFocus.focus(); } catch (e) {} }
  }

  function error(msg) {
    var box = modal.querySelector('[data-ndh-error]');
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  }

  function renderPreview() {
    var box = modal.querySelector('[data-ndh-preview]');
    if (!box) return;
    if (!state.items.length) { box.hidden = true; return; }

    if (state.items.length > 1) {
      box.hidden = false;
      box.querySelector('[data-ndh-preview-title]').textContent = state.items.length + ' items from your wishlist';
      box.querySelector('[data-ndh-preview-meta]').textContent = 'They can pick and choose';
      var imgM = box.querySelector('[data-ndh-preview-img]');
      fetch('/products/' + state.items[0].handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) { imgM.src = thumb(p.featured_image, 160); })
        .catch(function () {});
      return;
    }

    fetch('/products/' + state.items[0].handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (p) {
        var variant = state.items[0].variantId
          ? (p.variants || []).filter(function (v) { return String(v.id) === String(state.items[0].variantId); })[0]
          : null;
        box.hidden = false;
        box.querySelector('[data-ndh-preview-img]').src = thumb(p.featured_image, 160);
        box.querySelector('[data-ndh-preview-title]').textContent = p.title;
        box.querySelector('[data-ndh-preview-meta]').textContent =
          [variant && variant.title !== 'Default Title' ? variant.title : '', money(variant ? variant.price : p.price)]
            .filter(Boolean).join(' · ');
      })
      .catch(function () { box.hidden = true; });
  }

  /* ---------- submit ----------------------------------------------- */

  function submit(form) {
    error('');
    var fields = {
      to: (form.to_name.value || '').trim(),
      from: (form.from_name.value || '').trim(),
      email: (form.to_email.value || '').trim(),
      note: (form.note.value || '').trim()
    };

    [form.to_name, form.from_name, form.to_email].forEach(function (el) { el.removeAttribute('aria-invalid'); });

    if (!fields.to) { form.to_name.setAttribute('aria-invalid', 'true'); form.to_name.focus(); return error('Who are we sending this to? A first name is plenty.'); }
    if (!fields.from) { form.from_name.setAttribute('aria-invalid', 'true'); form.from_name.focus(); return error('Add your first name so they know who it’s from.'); }
    if (!validEmail(fields.email)) { form.to_email.setAttribute('aria-invalid', 'true'); form.to_email.focus(); return error('That email doesn’t look quite right — mind checking it?'); }
    if (!state.items.length) { return error('We couldn’t work out which product to hint at. Try reloading the page.'); }

    try { localStorage.setItem(NAME_KEY, fields.from); } catch (e) {}

    var btn = modal.querySelector('[data-ndh-submit]');
    var label = modal.querySelector('[data-ndh-submit-label]');
    var prev = label ? label.textContent : '';
    if (btn) btn.disabled = true;
    if (label) label.textContent = 'Sending…';

    lookup(state.items)
      .then(function (products) {
        if (!products.length) throw new Error('no products');
        var url = hintUrl(fields);
        state.url = url;
        return sendEvent(fields, products, url).then(function () { return products; });
      })
      .then(function (products) {
        var to = modal.querySelector('[data-ndh-sent-to]');
        if (to) to.textContent = fields.email;
        step('done');
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'drop_a_hint',
            hint_source: state.source,
            hint_items: products.length,
            hint_value: products.reduce(function (a, p) { return a + (Number(p.Price) || 0); }, 0)
          });
        } catch (e) {}
      })
      .catch(function (err) {
        /* The link still works even if Klaviyo refused — offer the manual path
           rather than a dead end. */
        state.url = state.url || hintUrl(fields);
        error('We couldn’t send that email just now. You can still copy the link below and send it yourself.');
        var to = modal.querySelector('[data-ndh-sent-to]');
        if (to) to.textContent = fields.email;
        step('done');
        var tick = modal.querySelector('.ndh__tick');
        if (tick) tick.hidden = true;
        if (window.console && console.warn) console.warn('[drop-a-hint]', err);
      })
      .finally(function () {
        if (btn) btn.disabled = false;
        if (label) label.textContent = prev || 'Send the hint';
      });
  }

  /* ---------- init -------------------------------------------------- */

  function init() {
    var all = document.querySelectorAll('#norsu-hint-modal');
    if (!all.length) return;
    modal = all[0];
    /* Two renders on one page (PDP + a section) would duplicate ids — keep the first. */
    for (var i = 1; i < all.length; i++) all[i].remove();

    try { cfg = JSON.parse(modal.getAttribute('data-ndh-config') || '{}'); } catch (e) { cfg = {}; }
    if (!cfg.companyId) return;

    /* Escape .norsu-pdp's reset and any transformed ancestor. */
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var form = modal.querySelector('form[data-ndh-step="compose"]');

    /* Prefill the sender's own name from last time. */
    try {
      var remembered = localStorage.getItem(NAME_KEY);
      if (remembered && form && !form.from_name.value) form.from_name.value = remembered;
    } catch (e) {}

    /* Triggers */
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-ndh-open]');
      if (!t) return;
      e.preventDefault();

      state.source = t.getAttribute('data-ndh-source') || 'pdp';
      if (state.source === 'wishlist') {
        state.items = wishlistHandles().slice(0, cfg.maxItems || 8).map(function (h) {
          return { handle: h, variantId: '' };
        });
      } else {
        var h = t.getAttribute('data-ndh-handle');
        state.items = h ? [{ handle: h, variantId: selectedVariant(t) }] : [];
      }

      if (!state.items.length) {
        /* Wishlist is empty — nothing to hint at. */
        error('Save a few favourites first, then drop your hint.');
      } else {
        error('');
      }
      step('compose');
      var tick = modal.querySelector('.ndh__tick');
      if (tick) tick.hidden = false;
      renderPreview();
      open();
    });

    /* Close */
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-ndh-close]')) { e.preventDefault(); close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    /* Submit */
    if (form) {
      form.addEventListener('submit', function (e) { e.preventDefault(); submit(form); });
      var note = form.note, count = modal.querySelector('[data-ndh-count]');
      if (note && count) {
        note.addEventListener('input', function () { count.textContent = note.value.length; });
      }
    }

    /* Copy link */
    var copy = modal.querySelector('[data-ndh-copy]');
    if (copy) {
      copy.addEventListener('click', function () {
        var url = state.url;
        if (!url) return;
        var lbl = modal.querySelector('[data-ndh-copy-label]');
        function done() {
          copy.setAttribute('data-copied', 'true');
          if (lbl) lbl.textContent = 'Link copied';
          setTimeout(function () {
            copy.removeAttribute('data-copied');
            if (lbl) lbl.textContent = 'Copy the link';
          }, 2600);
        }
        if (navigator.share) {
          navigator.share({ title: 'A little hint', url: url }).catch(function () {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, done);
        } else {
          window.prompt('Copy this link:', url);
        }
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
